import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const key = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !key) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.local for ' +
      'local work, or set both in the Cloudflare Pages project for a deploy.',
  )
}

export const supabase = createClient(url, key, {
  auth: {
    // PKCE is the right choice for a static-site OAuth round trip. Invitation
    // and password-reset emails arrive in a browser that never started the
    // flow, so they cannot exchange a PKCE code - instead the send-email-hook
    // Edge Function links them to /auth/confirm with a token_hash + type, and
    // AuthConfirm calls verifyOtp with those explicitly.
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
})

export type AppRole = 'ktm_admin' | 'ktm_staff' | 'org_admin' | 'facilitator' | 'participant'
export type PartnerTier = 'core' | 'supported' | 'partner_plus'
export type SubscriptionStatus = 'pending' | 'active' | 'grace' | 'lapsed' | 'cancelled'
export type Toolkit = 'programme' | 'training' | 'promotion' | 'impact'

export interface Organisation {
  id: string
  name: string
  slug: string
  tier: PartnerTier
  purchaser_type: 'community' | 'corporate'
  subscription_status: SubscriptionStatus
  renews_at: string | null
  grace_ends_at: string | null
  data_agreement_signed_at: string | null
}

/** The full organisations row, for the per-organisation admin screen. */
export interface OrganisationDetail extends Organisation {
  subscription_start: string | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface Profile {
  id: string
  org_id: string | null
  role: AppRole
  full_name: string | null
  email: string
  is_active: boolean
  auth_method: string
  organisations: Organisation | null
}

export interface Invitation {
  id: string
  email: string
  org_id: string
  role: AppRole
  created_at: string
  expires_at: string
  accepted_at: string | null
  invited_by: string | null
}

export interface Resource {
  id: string
  title: string
  description: string | null
  toolkit: Toolkit
  min_tier: PartnerTier
  kind: 'document' | 'video' | 'template'
  version: string
  version_notes: string | null
  published_at: string | null
  is_published: boolean
  storage_path: string | null
  file_size_bytes: number | null
  video_url: string | null
  section_id: string | null
  sort_order: number
}

export type ResourceKind = Resource['kind']

/** A heading the library is organised under, in the order admins set. */
export interface ResourceSection {
  id: string
  title: string
  description: string | null
  sort_order: number
}

export interface EvaluationField {
  key: string
  label: string
  type: 'scale' | 'boolean' | 'number' | 'text'
  min?: number
  max?: number
  required?: boolean
}

export interface EvaluationForm {
  id: string
  code: string
  title: string
  description: string | null
  audience: string
  schema: EvaluationField[]
  version: string
}

export interface EvaluationSubmission {
  id: string
  form_id: string
  org_id: string
  cohort_ref: string | null
  participant_ref: string | null
  answers: Record<string, unknown>
  status: 'draft' | 'submitted'
  consent_given: boolean
  submitted_at: string | null
  created_at: string
}

export const TIER_RANK: Record<PartnerTier, number> = {
  core: 1,
  supported: 2,
  partner_plus: 3,
}

export const TIER_LABEL: Record<PartnerTier, string> = {
  core: 'Core',
  supported: 'Supported',
  partner_plus: 'Partner Plus',
}

export const TOOLKIT_LABEL: Record<Toolkit, string> = {
  programme: 'Programme',
  training: 'Training',
  promotion: 'Promotion',
  impact: 'Impact',
}

export const ROLE_LABEL: Record<AppRole, string> = {
  ktm_admin: 'Admin',
  ktm_staff: 'Staff',
  org_admin: 'Organisation admin',
  facilitator: 'Facilitator',
  participant: 'Participant',
}
